import type { FilterService } from '../shared/filter.service';
import type { SearchService } from '../shared/search.service';
import type { UiStateService } from '../shared/ui-state.service';
import type { ScrollPositionService } from '../shared/scroll-position.service';

export const SHUNTING_LIST_SNAPSHOT_KEY = 'dgag_shunting_list_snapshot';
export const SHUNTING_EXPECT_RESTORE_KEY = 'dgag_shunting_expect_restore';

/** Same desktop "Show rows" values as Receiver — keep in sync with desktop-table-templates rows dropdown */
const DESKTOP_SHOW_ROWS_OPTIONS = new Set([10, 25, 50, 100, 150, 200, 250]);

export function normalizeShuntingDesktopShowRows(size: number): number {
  if (DESKTOP_SHOW_ROWS_OPTIONS.has(size)) {
    return size;
  }
  return 10;
}

export interface ShuntingListSnapshot {
  version: 1;
  filters: Record<string, string[]>;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  passportId: string;
  scrollY?: number;
}

export interface LeaveShuntingPersonaListStateDeps {
  filterService: FilterService;
  searchService: SearchService;
  ui: UiStateService;
  scrollPosition: ScrollPositionService;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isFiltersRecord(value: unknown): value is Record<string, string[]> {
  if (!isRecord(value)) {
    return false;
  }
  for (const v of Object.values(value)) {
    if (!isStringArray(v)) {
      return false;
    }
  }
  return true;
}

function isShuntingListSnapshot(value: unknown): value is ShuntingListSnapshot {
  if (!isRecord(value)) {
    return false;
  }
  if (value['version'] !== 1) {
    return false;
  }
  if (!isFiltersRecord(value['filters'])) {
    return false;
  }
  if (typeof value['searchQuery'] !== 'string') {
    return false;
  }
  if (typeof value['currentPage'] !== 'number' || !Number.isFinite(value['currentPage'])) {
    return false;
  }
  if (typeof value['itemsPerPage'] !== 'number' || !Number.isFinite(value['itemsPerPage'])) {
    return false;
  }
  if (typeof value['sortColumn'] !== 'string') {
    return false;
  }
  const dir = value['sortDirection'];
  if (dir !== 'asc' && dir !== 'desc') {
    return false;
  }
  if (typeof value['passportId'] !== 'string') {
    return false;
  }
  const scrollY = value['scrollY'];
  if (scrollY !== undefined && (typeof scrollY !== 'number' || !Number.isFinite(scrollY))) {
    return false;
  }
  return true;
}

export function peekShuntingExpectRestore(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(SHUNTING_EXPECT_RESTORE_KEY) === '1';
  } catch {
    return false;
  }
}

export function hasShuntingListSnapshot(): boolean {
  try {
    const raw = globalThis.sessionStorage?.getItem(SHUNTING_LIST_SNAPSHOT_KEY);
    return typeof raw === 'string' && raw.length > 0;
  } catch {
    return false;
  }
}

export function clearShuntingListRestoreState(): void {
  try {
    globalThis.sessionStorage?.removeItem(SHUNTING_LIST_SNAPSHOT_KEY);
    globalThis.sessionStorage?.removeItem(SHUNTING_EXPECT_RESTORE_KEY);
  } catch {
    // sessionStorage may be unavailable (SSR / private mode)
  }
}

export function saveShuntingListSnapshot(snapshot: ShuntingListSnapshot): void {
  try {
    globalThis.sessionStorage?.setItem(SHUNTING_LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function parseShuntingListSnapshot(): ShuntingListSnapshot | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(SHUNTING_LIST_SNAPSHOT_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isShuntingListSnapshot(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function leaveShuntingPersonaListState(deps: LeaveShuntingPersonaListStateDeps): void {
  clearShuntingListRestoreState();
  deps.filterService.clear();
  deps.searchService.clear();
  deps.ui.setSearchOpen(false);
  deps.ui.setFiltersOpen(false);
  deps.ui.setStatusSelected('pending');
  deps.scrollPosition.clearScrollPosition('/shunting');
}
